import './touch.css';

import { hudBar, iconButton } from './hud.js';
import { uiText } from './text.js';
import { onTouchChange } from './touch.js';

/**
 * The HUD on a touch screen (ADR 0043). The same buttons as on a desktop,
 * rearranged for two thumbs held sideways:
 *
 * - top right, the map, the bag and a « menu » button; every other HUD
 *   button (portals, guild, friends, sound, profile, shop,
 *   settings, the online count) goes into the drawer that button opens. The
 *   panels keep adding their buttons to `hudBar()` as before; this class
 *   moves them, so no panel knows about phones.
 * - bottom right, under the right thumb, the quick potion and, under it, the
 *   mount (R), shown while one is worn; the spell bar sits beside them
 *   (touch.css). No « attack » button:
 *   touching a creature walks to it and strikes, which is what Space does
 *   with two presses.
 *
 * On a mouse and keyboard nothing shows and every button is back in its row.
 */

/** The HUD buttons that stay in sight on a phone; the rest waits in the drawer. */
const PRIMARY_ACTIONS = new Set(['map', 'inventory']);

export interface TouchHudOptions {
  /** The W key's action: get on the worn mount, or off it. */
  onMount: () => void;
}

export class TouchHud {
  private readonly actions = document.createElement('div');
  private readonly mountButton: HTMLButtonElement;
  /** The spell bar's cooldown wedge over the mount, while the combat lock holds. */
  private readonly mountSweep: HTMLElement;
  private mountLock = -1;
  private readonly menuButton: HTMLButtonElement;
  private readonly drawer = document.createElement('div');
  private readonly observer: MutationObserver;
  private touch = false;
  private mountState = '';
  private readonly unsubscribe: () => void;

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.drawer.hidden) this.closeDrawer();
  };
  private readonly onOutside = (event: PointerEvent): void => {
    if (this.drawer.hidden) return;
    const target = event.target as Node | null;
    if (target !== null && (this.drawer.contains(target) || this.menuButton.contains(target)))
      return;
    this.closeDrawer();
  };

  constructor(options: TouchHudOptions) {
    this.actions.className = 'touch-actions';
    this.mountButton = iconButton('mount', 'Monture');
    this.mountButton.classList.add('touch-actions__mount');
    this.mountButton.hidden = true;
    this.mountSweep = document.createElement('span');
    this.mountSweep.className = 'spell-bar__sweep';
    this.mountSweep.hidden = true;
    this.mountButton.append(this.mountSweep);
    this.mountButton.addEventListener('click', options.onMount);
    this.actions.append(this.mountButton);
    document.body.append(this.actions);

    this.menuButton = iconButton('menu', 'Menu');
    this.menuButton.classList.add('hud-menu');
    this.menuButton.setAttribute('aria-expanded', 'false');
    this.menuButton.addEventListener('click', () => {
      if (this.drawer.hidden) this.openDrawer();
      else this.closeDrawer();
    });

    this.drawer.className = 'hud-drawer';
    this.drawer.hidden = true;
    this.drawer.setAttribute('role', 'menu');
    this.drawer.setAttribute('aria-label', uiText('Menu'));
    // A button in the drawer opens its window: the drawer makes way for it.
    this.drawer.addEventListener('click', (event) => {
      if ((event.target as HTMLElement).closest('button') !== null) this.closeDrawer();
    });
    document.body.append(this.drawer);

    this.observer = new MutationObserver(() => {
      this.arrange();
    });
    for (const corner of ['top', 'bottom'] as const) {
      this.observer.observe(hudBar(corner), { childList: true });
    }
    window.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('pointerdown', this.onOutside, true);
    this.unsubscribe = onTouchChange((touch) => {
      this.touch = touch;
      this.arrange();
    });
  }

  /** The quick potion's touch button (ui/quick-potion.ts), over the mount. */
  addPotion(button: HTMLElement): void {
    this.actions.prepend(button);
  }

  /**
   * The mount button shows while a mount is worn; « riding » lights it up.
   * Called every frame: the page is only touched when something changed.
   */
  setMount(worn: boolean, riding: boolean): void {
    const state = `${String(worn)}:${String(riding)}`;
    if (state === this.mountState) return;
    this.mountState = state;
    this.mountButton.hidden = !worn;
    this.mountButton.classList.toggle('touch-actions__mount--riding', riding);
  }

  /** What is left of the combat lock (0 to 1), darkening the mount like a spell's cooldown. */
  setMountLock(share: number): void {
    const degrees = Math.round(Math.min(1, Math.max(0, share)) * 360);
    if (degrees === this.mountLock) return;
    this.mountLock = degrees;
    this.mountSweep.style.setProperty('--left', `${String(degrees)}deg`);
    this.mountSweep.hidden = degrees === 0;
  }

  destroy(): void {
    this.observer.disconnect();
    this.unsubscribe();
    window.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('pointerdown', this.onOutside, true);
    // Back in their rows, for whatever HUD outlives the scene.
    this.touch = false;
    this.arrange();
    this.actions.remove();
    this.menuButton.remove();
    this.drawer.remove();
  }

  /**
   * Puts every HUD button where this device wants it. Idempotent: the
   * observer fires on its own moves too, and a second pass moves nothing.
   */
  private arrange(): void {
    const top = hudBar('top');
    const bottom = hudBar('bottom');
    if (!this.touch) {
      for (const element of [...this.drawer.children, ...top.children, ...bottom.children]) {
        if (!(element instanceof HTMLElement) || element === this.menuButton) continue;
        // Never moved (no home noted): it is already in its own row.
        const noted = element.dataset.hudHome;
        if (noted === undefined) continue;
        const home = noted === 'bottom' ? bottom : top;
        if (element.parentElement !== home) home.append(element);
      }
      this.menuButton.remove();
      this.closeDrawer();
      return;
    }
    for (const [bar, corner] of [
      [top, 'top'],
      [bottom, 'bottom'],
    ] as const) {
      for (const element of [...bar.children]) {
        if (!(element instanceof HTMLElement) || element === this.menuButton) continue;
        element.dataset.hudHome ??= corner;
        const primary = PRIMARY_ACTIONS.has(element.dataset.action ?? '');
        const place = primary ? top : this.drawer;
        if (element.parentElement !== place) place.append(element);
      }
    }
    if (this.menuButton.parentElement !== top) top.append(this.menuButton);
  }

  private openDrawer(): void {
    this.drawer.hidden = false;
    this.menuButton.setAttribute('aria-expanded', 'true');
    this.menuButton.classList.add('hud-menu--open');
  }

  private closeDrawer(): void {
    this.drawer.hidden = true;
    this.menuButton.setAttribute('aria-expanded', 'false');
    this.menuButton.classList.remove('hud-menu--open');
  }
}
